import { PR_BUILD_STATUS } from "../../utils/constants";

// Commits carried over from an older release branch are prefixed in their message.
const DOUBLE_COMMIT_PREFIX = /^\s*double commit\s*:\s*/i;

/**
 * @param {string} message
 * @returns {boolean} */
const isDoubleCommit = (message = "") =>
  DOUBLE_COMMIT_PREFIX.test(message || "");

/**
 * Whether a single commit was carried over from an earlier release. The backend
 * flag is the source of truth; the message prefix is a fallback for older rows.
 * A commit that is neither is "original" to this branch.
 * @param {any} commit
 * @returns {boolean} */
export const isCommitDouble = (commit) =>
  Boolean(commit?.is_double_commit) || isDoubleCommit(commit?.commit_message);

/**
 * Message without the "Double Commit:" prefix; the per-commit DOUBLE tag and the
 * service row badge already carry that state, so the text stays about the change.
 * @param {string} message
 * @returns {string} */
export const commitMessageText = (message = "") =>
  (message || "").replace(DOUBLE_COMMIT_PREFIX, "");

/**
 * Pending double-commit PRs that target the branch of one release.
 * @param {any[]} prs
 * @param {string} releaseName e.g. rel-147
 * @returns {any[]} */
export const doubleCommitPrs = (prs, releaseName) => {
  const releaseVersion = String(releaseName || "").split("-")[1];
  if (!releaseVersion) return [];
  return (prs || []).filter(
    (pr) => pr?.base_branch?.split(".")?.[1] === releaseVersion
  );
};

/**
 * Every service of the release with the state its collapsed row has to show.
 * @param {any[]} repositories
 * @param {any[]} pendingDoublePrs
 * @returns {any[]} */
export const buildServices = (repositories = [], pendingDoublePrs = []) =>
  (repositories || []).map((repository) => {
    const commits = repository?.commits || [];
    const name = repository?.repo_name;
    const pendingMerges = pendingDoublePrs.filter(
      (pr) => pr?.repository === name
    ).length;
    const doubleCommits = commits.filter(isCommitDouble).length;
    // Commits written for this branch, not carried over. A service with any of
    // these is the interesting one on an otherwise-inherited release.
    const native = commits.filter((commit) => !isCommitDouble(commit)).length;
    const failed = commits.filter(
      (commit) => commit?.build_status === PR_BUILD_STATUS[2]
    ).length;
    const pending = commits.filter(
      (commit) => commit?.build_status === PR_BUILD_STATUS[0]
    ).length;
    const successful = commits.filter(
      (commit) => commit?.build_status === PR_BUILD_STATUS[1]
    ).length;
    const double = doubleCommits || pendingMerges;
    return {
      name,
      commits,
      version: commits[0]?.commit_tag || "",
      double,
      // Real count of double commits on the branch, kept apart from `double`
      // (which also stands in for open DC PRs when the branch has no commits).
      doubleCommits,
      pendingMerges,
      native,
      failed,
      pending,
      successful,
      state: (double && "double") || (failed && "failed") || "neutral",
    };
  });

const plural = (count, noun) => `${count} ${noun}${count === 1 ? "" : "s"}`;

/**
 * Chips a collapsed service row shows, so a mixed service reads its full make-up
 * rather than only its most severe facet.
 *
 * Composition (double vs original) partitions the commits and always sums to
 * the total, so both sides can show together ("2 double commits · 1 original
 * commit"). Build status is surfaced per commit inside the expanded row, not on
 * the collapsed badge.
 * @param {any} service
 * @returns {{label: string, tone: string}[]} */
export const serviceBadges = (service) => {
  const total = service?.commits?.length || 0;
  const dc = service?.doubleCommits || 0;
  const native = service?.native || 0;
  const badges = [];

  if (dc && native) {
    badges.push({ label: plural(dc, "double commit"), tone: "double" });
    badges.push({ label: plural(native, "original commit"), tone: "neutral" });
  } else if (dc) {
    badges.push({ label: plural(dc, "double commit"), tone: "double" });
  } else if (total) {
    // No doubles here, so every commit is original to the branch.
    badges.push({ label: plural(total, "original commit"), tone: "neutral" });
  } else if (service?.pendingMerges) {
    // No commits on the branch yet, only an open double-commit PR.
    badges.push({
      label: plural(service.pendingMerges, "double commit") + " pending",
      tone: "double",
    });
  }

  return badges;
};

/**
 * Services matching a segment and a search query, out of those that have new commits.
 * @param {any[]} services
 * @param {string} segment
 * @param {string} query already lower-cased and trimmed
 * @returns {any[]} */
export const filterServices = (services = [], segment = "all", query = "") =>
  services.filter((service) => {
    if (query && !service?.name?.toLowerCase().includes(query)) return false;
    if (segment === "double") return Boolean(service?.double);
    if (segment === "native") return Boolean(service?.native);
    if (segment === "failed") return Boolean(service?.failed);
    if (segment === "pending") return Boolean(service?.pending);
    if (segment === "successful") return Boolean(service?.successful);
    return true;
  });

/**
 * Counts behind the filter segments; every count is a number of services.
 * @param {any[]} withCommits services that have at least one new commit
 * @param {number} total every service of the release
 * @returns {{all: number, commits: number, double: number, native: number, failed: number, pending: number, successful: number}} */
export const serviceCounts = (withCommits = [], total = 0) => ({
  all: total,
  commits: withCommits.length,
  double: withCommits.filter((service) => service?.double).length,
  native: withCommits.filter((service) => service?.native).length,
  failed: withCommits.filter((service) => service?.failed).length,
  pending: withCommits.filter((service) => service?.pending).length,
  successful: withCommits.filter((service) => service?.successful).length,
});

// Above this share of services carrying double commits, the branch is treated as
// inherited: double commits are the norm, so the banner flips from warning about
// them to pointing at the services that carry original work.
const DOUBLE_DOMINANT_SHARE = 0.8;

/**
 * What the patch banner should say, or null for no banner. Three registers:
 *   - no double commits  -> null
 *   - doubles a minority  -> amber warning, filters to the affected services
 *   - doubles dominant    -> blue info; if any service carries original work it
 *                            points there, otherwise it just notes everything is
 *                            carried over.
 * @param {{all:number, commits:number, double:number, native:number}} counts
 * @param {string} releaseName e.g. rel-147
 * @returns {{tone:string, title:string, body:string, actionSegment:(string|null), actionLabel:(string|null)}|null} */
export const patchBanner = (counts, releaseName = "") => {
  if (!counts?.double) return null;
  const share = counts.commits ? counts.double / counts.commits : 0;

  if (share >= DOUBLE_DOMINANT_SHARE) {
    if (!counts.native) {
      return {
        tone: "info",
        title: `Everything in ${releaseName} is carried over`,
        body: `All ${counts.commits} services inherit their commits from an earlier release — nothing here is original to ${releaseName}. You can still create the patch.`,
        actionSegment: null,
        actionLabel: null,
      };
    }
    const services = plural(counts.native, "service");
    const carry = counts.native === 1 ? "carries" : "carry";
    const them = counts.native === 1 ? "it" : "them";
    return {
      tone: "info",
      title: `${services} to review in ${releaseName}`,
      body: `Most commits here are carried over from an earlier release. ${services} ${carry} work original to this branch — review ${them} before patching.`,
      actionSegment: "native",
      actionLabel: `Show ${counts.native === 1 ? "it" : "these"}`,
    };
  }

  return {
    tone: "warning",
    title: `Double commits are present for ${releaseName}`,
    body: `${counts.double} of ${counts.all} services are affected. You can still create the patch — untick any commit you want to leave out.`,
    actionSegment: "double",
    actionLabel: "Show only these",
  };
};

/**
 * Stable identity of one commit inside a patch, used to track deselection.
 * @param {string} repoName
 * @param {any} commit
 * @returns {string} */
export const commitKey = (repoName, commit) =>
  `${repoName}::${commit?.commit_hash || commit?.commit_tag}`;

const BUILD_SUCCESSFUL = PR_BUILD_STATUS[1];

/**
 * Repos whose head commit has not built successfully.
 *
 * "Head" is the newest commit still INCLUDED, not commits[0]: commits are
 * listed newest first and skipped from the top down, so skipping two makes
 * commits[2] the head. That head is what the patch builds and deploys from,
 * which is why the ones below it are not checked. Anything other than
 * SUCCESSFUL blocks — that covers both FAILED and IN_PROGRESS. A repo with
 * every commit skipped has no head and cannot block.
 * @param {any[]} services
 * @param {Record<string, number>} skipped commits omitted from the top, per repo
 * @returns {{name: string, status: string}[]} */
export const unbuiltSelectedHeads = (services = [], skipped = {}) =>
  (services || [])
    .map((service) => ({
      name: service.name,
      head: (service?.commits || [])[skipped[service.name] || 0],
    }))
    .filter(({ head }) => head && head.build_status !== BUILD_SUCCESSFUL)
    .map(({ name, head }) => ({ name, status: head.build_status }));

// Blocking repos named inline before the message spills into "and N more".
const NAMED_BLOCKERS = 2;

/**
 * Why the patch cannot be created. `text` is empty when it can.
 *
 * Returned in two parts so the caller can render the overflow ("and N more")
 * as its own hoverable element: `text` names the first couple of blocking
 * repos, `more` holds the ones that did not fit.
 *
 * Pending double commits deliberately do NOT block: they are surfaced as a
 * warning banner, and the user decides which commits go into the patch.
 * @param {{commits: number}} counts
 * @param {boolean} notReadOnly
 * @param {number} selectedCommits commits currently ticked for this patch
 * @param {{name: string, status: string}[]} unbuiltHeads
 * @returns {{text: string, more: {name: string, status: string}[]}} */
export const blockedReason = (
  counts,
  notReadOnly,
  selectedCommits = 0,
  unbuiltHeads = []
) => {
  const plain = (text) => ({ text, more: [] });

  if (!notReadOnly)
    return plain(
      "Read only \u2014 you cannot create a patch for this release."
    );
  if (!counts.commits) return plain("No new commits to patch");
  if (!selectedCommits) return plain("Select at least one commit to patch");

  if (unbuiltHeads.length) {
    const named = unbuiltHeads.slice(0, NAMED_BLOCKERS);
    const more = unbuiltHeads.slice(NAMED_BLOCKERS);
    // One condition (head is not SUCCESSFUL), but the wording tells the user
    // whether to wait for the build or go fix it.
    const stillRunning = unbuiltHeads.every(
      ({ status }) => status === PR_BUILD_STATUS[0]
    );
    const verb = stillRunning ? "still in progress" : "not successful";
    const names = named.map(({ name }) => name);
    return {
      text: `Head build ${verb} for ${
        more.length ? names.join(", ") : names.join(" and ")
      }`,
      more,
    };
  }

  return plain("");
};
